import { useEffect, useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { useAppStore } from '../store/appStore'
import { isReadOnly } from '../models/UserModel'
import { breadcrumb } from '../utils/sentry'
import { get } from '../api/api'

interface IntercomSettings {
    app_id: string
    intercom_user_jwt: string
    action_color?: string
    background_color?: string
    hide_default_launcher?: boolean
}

type IntercomArgument = IntercomSettings | ((unreadCount: number) => void)

declare global {
    interface Window {
        Intercom?: (command: string, argument?: IntercomArgument) => void
        intercomSettings?: IntercomSettings
    }
}

const INTERCOM_APP_ID = 'wx5opiwa'
const INTERCOM_ACCENT_COLOR = '#B45C44'
const WIDGET_POLL_INTERVAL_MS = 200
const WIDGET_POLL_ATTEMPTS = 25

// The widget script can fire `onload` before it has defined `window.Intercom`, while a blocked or
// neutered script never defines it at all. Poll for a bounded while, then give up so the sidebar
// never offers an item that cannot open the messenger.
const waitForWidget = (onAvailable: () => void): (() => void) => {
    let attemptsLeft = WIDGET_POLL_ATTEMPTS
    let timer = 0

    const check = (): void => {
        if (window.Intercom) {
            onAvailable()
            return
        }

        attemptsLeft -= 1
        if (attemptsLeft === 0) {
            console.error('[useIntercom] Script loaded but window.Intercom is missing')
            return
        }

        timer = window.setTimeout(check, WIDGET_POLL_INTERVAL_MS)
    }

    check()

    return () => window.clearTimeout(timer)
}

export const useIntercom = () => {
    const idToken = useAuthStore((state) => state.idToken)
    const userModel = useAppStore((state) => state.userModel)
    const [widgetAvailable, setWidgetAvailable] = useState(() => !!window.Intercom)
    const [intercomUserJwt, setIntercomUserJwt] = useState<string | null>(null)

    useEffect(() => {
        if (window.Intercom) {
            console.info('[useIntercom] Intercom already loaded')
            return
        }

        console.info('[useIntercom] Loading Intercom script')

        let stopWaiting = () => {}

        const script = document.createElement('script')
        script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
        script.async = true
        script.onload = () => {
            console.info('[useIntercom] Intercom script loaded')
            stopWaiting = waitForWidget(() => setWidgetAvailable(true))
        }

        document.body.appendChild(script)

        return () => stopWaiting()
    }, [])

    useEffect(() => {
        const fetchIntercomToken = async () => {
            // Waits for /me: until the user is known an offboarded one is indistinguishable
            // from an active one, and the token exists well before /me resolves — asking in
            // that window is what 403'd on every load. The effect re-runs when it lands.
            if (!idToken || !widgetAvailable || intercomUserJwt || !userModel) return
            // An offboarded user is not allowed this token, and the widget cannot open for
            // them anyway.
            if (isReadOnly(userModel)) return

            try {
                const token = await get<string>('/me/intercom-token')
                setIntercomUserJwt(token)
            } catch (error) {
                console.error('[useIntercom] Failed to fetch Intercom token:', error)
            }
        }

        fetchIntercomToken()
    }, [idToken, intercomUserJwt, widgetAvailable, userModel])

    useEffect(() => {
        const intercom = window.Intercom
        if (!intercomUserJwt || !intercom) return

        window.intercomSettings = {
            app_id: INTERCOM_APP_ID,
            intercom_user_jwt: intercomUserJwt,
            action_color: INTERCOM_ACCENT_COLOR,
            background_color: INTERCOM_ACCENT_COLOR,
            // The floating launcher lands misaligned over the app UI, so the sidebar menu
            // item is the only entry point to the messenger.
            hide_default_launcher: true,
        }

        intercom('boot', window.intercomSettings)
        // Hiding the launcher also hides its unread badge, so mirror the count into the store
        // for the sidebar item to show.
        intercom('onUnreadCountChange', (unreadCount) => useAppStore.setState({ intercomUnreadCount: unreadCount }))
        useAppStore.setState({ isIntercomReady: true })
        console.info('[useIntercom] Intercom booted with JWT')
        breadcrumb('useIntercom', 'Intercom booted')
    }, [intercomUserJwt])
}

export const openIntercomMessenger = (): void => {
    window.Intercom?.('show')
}
